import type { ColorSpec } from './types';
import { aurora } from './aurora';
import { black } from './black';
import { blue } from './blue';
import { cream } from './cream';
import { forest } from './forest';
import { gray } from './gray';
import { green } from './green';
import { purple } from './purple';
import { red } from './red';
import { white } from './white';
import { yellow } from './yellow';

/**
 * Color registry — single source of truth for Button color specs.
 *
 * Adding a new color:
 *   1. Add the Tailwind palette to `tailwind.config.ts` (50–950 shades).
 *   2. Create `./<color>.ts` exporting a `ColorSpec` (copy `blue.ts`).
 *   3. Register it below.
 *   4. Done — every Input2 consumer (Button, ButtonGroup, ButtonSegment,
 *      FilterPopover, Input, etc.) picks it up automatically.
 *
 * The registry powers a derived `ColorVariant` union, so the type system
 * automatically extends to recognize `${color}` / `${color}Outline` /
 * `${color}Soft`.
 */
export const colorRegistry = {
  blue,
  red,
  green,
  yellow,
  purple,
  aurora,
  forest,
  cream,
  black,
  white,
  gray,
} as const satisfies Record<string, ColorSpec>;

export type ColorName = keyof typeof colorRegistry;

export type Style = 'filled' | 'outline' | 'soft';

export type ColorVariant = ColorName | `${ColorName}Outline` | `${ColorName}Soft`;

export type ParsedVariant = { kind: 'color'; color: ColorName; style: Style };

/**
 * Compose an orthogonal `(color, tone)` pair into the underlying ColorVariant
 * string. Powers the orthogonal Button API — components that accept `color`
 * and `tone` as separate props normalize them through this helper before
 * passing the result to the existing class builders.
 *
 *   composeVariant('blue', 'soft')      → 'blueSoft'
 *   composeVariant('forest', 'filled') → 'forest'
 *   composeVariant('cream')             → 'cream'  (filled is the default)
 */
export function composeVariant(color: ColorName, tone: Style = 'filled'): ColorVariant {
  if (tone === 'filled') return color;
  if (tone === 'outline') return `${color}Outline` as ColorVariant;
  return `${color}Soft` as ColorVariant;
}

/** Turn a ColorVariant into its `{ color, style }` parts. */
export function parseVariant(variant: ColorVariant): ParsedVariant {
  if (variant.endsWith('Outline')) {
    const color = variant.slice(0, -'Outline'.length) as ColorName;
    if (color in colorRegistry) return { kind: 'color', color, style: 'outline' };
  }
  if (variant.endsWith('Soft')) {
    const color = variant.slice(0, -'Soft'.length) as ColorName;
    if (color in colorRegistry) return { kind: 'color', color, style: 'soft' };
  }
  if (variant in colorRegistry) {
    return { kind: 'color', color: variant as ColorName, style: 'filled' };
  }
  // Type system prevents this, but be safe at runtime — fall back to gray.
  return { kind: 'color', color: 'gray', style: 'filled' };
}

export type { ColorSpec, InputColorSet } from './types';
